import { ChartUnit } from '../../../../core/types/chart-unit.js';
import { HistogramBin } from '../../../types/histogram.js';

export type CumulativePoint = {
  binsCumulative: number;
  baseCumulative: number;
  seriesId: string;
};

export type MapStackedPoint = Map<string, CumulativePoint>;

/**
 * Gets the stacked key for the mapStackedPoints Map
 *
 * @param bin - Current bin
 * @param unit - Current series unit
 * @returns - Stacked key to access the mapStackedPoints
 */
export function getStackedKeyByUnit(bin: HistogramBin, unit: ChartUnit) {
  return (
    bin.from.toString() +
    Math.sign(bin.value).toString() +
    bin.from.toString() +
    bin.to.toString() +
    //TODO: Pending to apply hash function from this PR: https://bitbucket.lab.dynatrace.org/projects/APPFW/repos/design-system/pull-requests/7188/overview?commentId=1144690
    (unit ?? '').toString()
  );
}

/**
 * Gets the cumulative point value from the mapStackedPoints given the new value.
 *
 * @param mapStackedPoints - Given MapStackedPoint map
 * @param bin - Current datapoint
 * @param unit - Current series unit
 * @param seriesId - Current series id
 * @param value - Current modified value of the datapoint
 * @returns - CumulativePoint
 */
export function getCumulativeValue(
  mapStackedPoints: MapStackedPoint,
  bin: HistogramBin,
  unit: ChartUnit,
  seriesId: string,
  value: number = 0,
) {
  const key = getStackedKeyByUnit(bin, unit);

  const stackedBin = mapStackedPoints.get(key);

  let binsCumulative = 0;
  let baseCumulative = 0;
  let cumulativeValue = value;

  if (stackedBin) {
    const {
      binsCumulative: prevBinsCumulative,
      baseCumulative: prevBaseCumulative,
      seriesId: prevSeriesId,
    } = stackedBin;

    binsCumulative = prevBinsCumulative ?? 0;
    baseCumulative = prevBaseCumulative ?? 0;

    const baseCumulativeHasToChange = prevSeriesId !== seriesId;

    if (baseCumulativeHasToChange) {
      baseCumulative = binsCumulative;
    }

    cumulativeValue = baseCumulative + value;
  }

  binsCumulative =
    stackedBin?.seriesId !== seriesId
      ? cumulativeValue
      : Math.sign(value) > 0
        ? Math.max(cumulativeValue, binsCumulative)
        : Math.min(cumulativeValue, binsCumulative);

  return {
    cumulativeValue,
    binsCumulative,
    baseCumulative,
    key,
  };
}

/**
 * Set the new CumulativePoint to the mapStackedPoints
 *
 * @param mapStackedPoints - Given MapStackedPoint map
 * @param unit - Current series unit
 * @param seriesId - Current series id
 * @param binsCumulative - Current cumulative value of bins
 * @param baseCumulative - Current base cumulative value
 */
export function setCumulativeBins(
  mapStackedPoints: MapStackedPoint,
  seriesId: string,
  key: string,
  binsCumulative: number,
  baseCumulative: number,
) {
  mapStackedPoints.set(key, {
    seriesId,
    binsCumulative,
    baseCumulative,
  });
}
